import { getActivities } from "@grudder/apiCalls";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { reset } from "./reset";

interface ActivitiesState {
  dashboardActivities: any[];
  leadActivities: { [leadId: string]: any[] };
  total: number;
  leadTotal: { [leadId: string]: number };
  loading: boolean;
  loadingMore: boolean;
  leadLoading: { [leadId: string]: boolean };
  lastFetchedAt: number | null;
  leadLastFetchedAt: { [leadId: string]: number | null };
  currentPage: number;
}

const initialState: ActivitiesState = {
  dashboardActivities: [],
  leadActivities: {},
  total: 0,
  leadTotal: {},
  loading: false,
  loadingMore: false,
  leadLoading: {},
  lastFetchedAt: null,
  leadLastFetchedAt: {},
  currentPage: 0,
};

export const fetchDashboardActivities = createAsyncThunk("activities/fetchDashboardActivities", async ({ limit, page }: { limit: number; page: number }, { getState }) => {
  const state = getState() as { activities: ActivitiesState };
  const { dashboardActivities } = state.activities;

  // if (page === 0 && dashboardActivities.length > 0 && lastFetchedAt) {
  //     const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
  //     if (lastFetchedAt > fiveMinutesAgo) {
  //         return {
  //             data: dashboardActivities,
  //             total: state.activities.total,
  //             page: currentPage,
  //         };
  //     }
  // }

  // if (page > 0 && page <= currentPage && dashboardActivities.length > 0) {
  //     return {
  //         data: dashboardActivities,
  //         total: state.activities.total,
  //         page: currentPage,
  //     };
  // }

  const response = await getActivities(limit, page * limit, null);
  let resultData = response.data;
  if (dashboardActivities.length > 0) {
    resultData = [...dashboardActivities, ...response.data];
  }

  return {
    data: resultData,
    total: response.total,
    page,
  };
});

export const fetchLeadActivities = createAsyncThunk(
  "activities/fetchLeadActivities",
  async ({
    leadId,
    limit = 10,
    skip = 0,
    activityTypes
  }: {
    leadId: string;
    limit?: number;
    skip?: number;
    activityTypes?: string[]
  }, { getState }) => {
    const state = getState() as { activities: ActivitiesState };
    const existingActivities = state.activities.leadActivities[leadId];
    const lastFetchedAt = state.activities.leadLastFetchedAt[leadId];

    // Check if we have cached data that's still fresh (5 minutes)
    if (skip === 0 && existingActivities && existingActivities.length > 0 && lastFetchedAt) {
      const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
      if (lastFetchedAt > fiveMinutesAgo) {
        return {
          leadId,
          data: existingActivities,
          total: state.activities.leadTotal[leadId] || 0,
          skip,
        };
      }
    }

    const response = await getActivities(limit, skip, leadId, activityTypes);

    return {
      leadId,
      data: response.data,
      total: response.total,
      skip,
    };
  }
);

const activitiesSlice = createSlice({
  name: "activities",
  initialState,
  reducers: {
    resetActivitySlice: (state) => {
      state.dashboardActivities = [];
      state.leadActivities = {};
      state.total = 0;
      state.leadTotal = {};
      state.loading = false;
      state.loadingMore = false;
      state.leadLoading = {};
      state.lastFetchedAt = null;
      state.leadLastFetchedAt = {};
      state.currentPage = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardActivities.pending, (state, action) => {
        if (action.meta.arg.page === 0) {
          state.loading = true;
        } else {
          state.loadingMore = true;
        }
      })
      .addCase(fetchDashboardActivities.fulfilled, (state, action) => {
        state.loading = false;
        state.loadingMore = false;
        state.dashboardActivities = action.payload.data;
        state.total = action.payload.total;
        state.currentPage = action.payload.page;

        if (action.payload.page === 0) {
          state.lastFetchedAt = Date.now();
        }
      })
      .addCase(fetchDashboardActivities.rejected, (state) => {
        state.loading = false;
        state.loadingMore = false;
      })
      .addCase(fetchLeadActivities.pending, (state, action) => {
        const leadId = action.meta.arg.leadId;
        state.leadLoading[leadId] = true;
      })
      .addCase(fetchLeadActivities.fulfilled, (state, action) => {
        const { leadId, data, total, skip } = action.payload;
        state.leadLoading[leadId] = false;

        if (skip === 0) {
          // Fresh fetch - replace existing data
          state.leadActivities[leadId] = data;
          state.leadLastFetchedAt[leadId] = Date.now();
        } else {
          // Pagination - append to existing data
          state.leadActivities[leadId] = [...(state.leadActivities[leadId] || []), ...data];
        }

        state.leadTotal[leadId] = total;
      })
      .addCase(fetchLeadActivities.rejected, (state, action) => {
        const leadId = action.meta.arg.leadId;
        state.leadLoading[leadId] = false;
      })
      .addCase(reset, () => initialState);
  },
});

export const { resetActivitySlice } = activitiesSlice.actions;

export default activitiesSlice.reducer;
